import { useState, useCallback, useMemo } from 'react';

/** A single validation rule — a function that returns an error string or null */
export type ValidationRule = (value: string) => string | null;

/** Common validation rules */
export const validators = {
  required: (msg?: string): ValidationRule => (value) =>
    !value.trim() ? msg || 'This field is required' : null,

  email: (msg?: string): ValidationRule => (value) =>
    value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ? msg || 'Please enter a valid email address'
      : null,

  minLength: (min: number, msg?: string): ValidationRule => (value) =>
    value.length > 0 && value.length < min
      ? msg || `Must be at least ${min} characters`
      : null,

  maxLength: (max: number, msg?: string): ValidationRule => (value) =>
    value.length > max
      ? msg || `Must be no more than ${max} characters`
      : null,

  password: (msg?: string): ValidationRule => (value) => {
    if (!value) return null;
    if (value.length < 8) return msg || 'Must be at least 8 characters';
    if (!/[A-Z]/.test(value)) return msg || 'Must contain an uppercase letter';
    if (!/[a-z]/.test(value)) return msg || 'Must contain a lowercase letter';
    if (!/[0-9]/.test(value)) return msg || 'Must contain a number';
    return null;
  },

  match: (matchValue: string, label: string): ValidationRule => (value) =>
    value !== matchValue ? `${label} does not match` : null,

  phone: (msg?: string): ValidationRule => (value) =>
    value && !/^[\d\s\-+()]{7,20}$/.test(value)
      ? msg || 'Please enter a valid phone number'
      : null,

  url: (msg?: string): ValidationRule => (value) =>
    value && !/^https?:\/\/.+/i.test(value)
      ? msg || 'Please enter a valid URL'
      : null,
};

export interface FieldState {
  value: string;
  error: string | null;
  touched: boolean;
  dirty: boolean;
}

export interface FieldConfig {
  initialValue?: string;
  rules?: ValidationRule[];
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

/**
 * Hook for managing a single form field with validation.
 * Provides real-time error state, touched/dirty tracking, and debounced validation.
 */
export function useFormField(config: FieldConfig = {}) {
  const {
    initialValue = '',
    rules = [],
    validateOnChange = true,
    validateOnBlur = true,
  } = config;

  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [dirty, setDirty] = useState(false);

  const validate = useCallback(
    (val: string): string | null => {
      for (const rule of rules) {
        const err = rule(val);
        if (err) return err;
      }
      return null;
    },
    [rules],
  );

  const onChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      setDirty(true);
      if (validateOnChange) {
        setError(validate(newValue));
      }
    },
    [validate, validateOnChange],
  );

  const onBlur = useCallback(() => {
    setTouched(true);
    if (validateOnBlur) {
      setError(validate(value));
    }
  }, [validate, validateOnBlur, value]);

  const reset = useCallback(() => {
    setValue(initialValue);
    setError(null);
    setTouched(false);
    setDirty(false);
  }, [initialValue]);

  const isValid = useMemo(() => error === null && value.length > 0, [error, value]);
  const showError = useMemo(
    () => (touched || dirty) && error !== null,
    [touched, dirty, error],
  );

  return {
    value,
    error,
    touched,
    dirty,
    isValid,
    showError,
    onChange,
    onBlur,
    reset,
    validate: () => validate(value),
    setValue,
    setError,
  };
}

export type UseFormFieldReturn = ReturnType<typeof useFormField>;
